import { CheckCircleOutlined, CloseCircleOutlined } from "@ant-design/icons";
import { useSearch } from "@tanstack/react-router";
import { Tag } from "antd";
import {
    useGetConversionStatesExportMutation,
    useGetConversionStatesQuery,
} from "api/TenantSlug/ConversionState/hooks";
import { LogClickDrawerDetail } from "components/Drawers/Logs/LogClickDrawerDetail/LogClickDrawerDetail.tsx";
import { LogConversionDrawerDetail } from "components/Drawers/Logs/LogConversionDrawerDetail.tsx";
import { LogLeadDrawerDetail } from "components/Drawers/Logs/LogLeadDrawerDetail.tsx";
import { CodeEditor } from "components/UI/CodeEditor";
import { useConversionTypeSelectOptions } from "components/UI/Selects/ConversionTypeSelectSearchable/useConversionTypeSelectOptions.ts";
import { ShortenedIdWithCopy } from "components/UI/ShortenedIdWithCopy";
import { Table } from "components/UI/Table";
import { exportAtom } from "components/UI/Table/atoms/exportAtom";
import { DEFAULT_PAGE_NUMBER, DEFAULT_PAGE_SIZE, tableAtom } from "components/UI/Table/atoms/tableAtom";
import { TableColumn } from "components/UI/Table/columns/TableColumn.tsx";
import { FILTER_TYPES } from "components/UI/Table/constants/filter";
import { useTableDataApiOperations } from "components/UI/Table/hooks/useTableDataApiOperations";
import { TableColumnType } from "components/UI/Table/types/table";
import { ColumnHeaderTitle } from "components/UI/Table/UI/ColumnHeaderTitle";
import {
    CLICK_STATUS_VALUES,
    CONVERSION_STATUS_VALUES,
    ConversionStatusCell,
    LEAD_STATUS_VALUES,
} from "components/UI/Table/UI/ConversionStatusCell/ConversionStatusCell.tsx";
import { CountryCell } from "components/UI/Table/UI/CountryCell/CountryCell.tsx";
import { DateTimeCell } from "components/UI/Table/UI/DateTimeCell/DateTimeCell.tsx";
import { EntityLinkCell } from "components/UI/Table/UI/EntityLinkCell/EntityLinkCell.tsx";
import { LeadStateCell } from "components/UI/Table/UI/LeadStateCell/LeadStateCell.tsx";
import { UrlCell } from "components/UI/Table/UI/UrlCell/UrlCell.tsx";
import { useCreatedAtFilterValue } from "components/UI/Table/utils/getDefaultCreatedAtFilter";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { usePermission } from "hooks/usePermissions";
import { useSmartRowHandlers } from "hooks/useTableRowHandlers.tsx";
import { useUser } from "hooks/useUser.ts";
import { Key } from "react";
import { useRecoilValue } from "recoil";
import { ConversionStateType, PermissionEnum } from "types/enums";
import { DrawerId, TableId } from "types/models";
import { formatLongId } from "utils/formatLongId.ts";

import GetConversionStatesResponse = App.Http.Responses.ConversionState.GetConversionStatesResponse;
import ConversionStateData = App.Data.ConversionStateData;
import BaseExportRequest = App.Http.Requests.BaseExportRequest;

const NON_CONVERSION_TYPES = ["click", "lead", "unknown"];

interface ConversionLogsTableProps {
    type?: ConversionStateType | "conversion";
}
export const ConversionLogsTable = ({ type }: ConversionLogsTableProps) => {
    const { t } = usePaldockTranslation("tables.conversion-logs");
    const { t: tG } = usePaldockTranslation("tables.general-columns");
    const { t: tGL } = usePaldockTranslation("tables.general-labels");
    const { t: tGeneral } = usePaldockTranslation("general");
    const searchParams: { logClickDetail?: string; logLeadDetail?: string; logConversionDetail?: string } = useSearch({
        strict: false,
    });
    const tableId =
        type === ConversionStateType.Click
            ? TableId.ConversionClickLogsTable
            : type === ConversionStateType.Lead
              ? TableId.ConversionLeadLogsTable
              : TableId.ConversionLogsTable;
    const viewDetailPermission =
        type === ConversionStateType.Click
            ? PermissionEnum.ConversionClickLogDetailView
            : type === ConversionStateType.Lead
              ? PermissionEnum.ConversionLeadLogDetailView
              : PermissionEnum.ConversionConversionLogDetailView;
    const tableAtomInstance = tableAtom<ConversionStateData>();
    const tableState = useRecoilValue(tableAtomInstance(tableId));
    const exportState = useRecoilValue(exportAtom(tableId));
    const createdAtStr = useCreatedAtFilterValue();
    const permissionsUtils = usePermission();
    const userUtils = useUser();
    const isConversionView = type === "conversion";

    const { options: allConversionTypeOptions } = useConversionTypeSelectOptions();
    const conversionOnlyTypeOptions = allConversionTypeOptions.filter(
        (opt) => !NON_CONVERSION_TYPES.includes(opt.value.toLowerCase())
    );

    const include = isConversionView
        ? "country,owner,offer,advertiser,payouts,cascade"
        : type === ConversionStateType.Lead
          ? "country,owner,offer,advertiser,formSubmissionValues,cascadeProcess"
          : "country,owner,offer,advertiser,formSubmissionValues";

    const userFilter = tableState.filter?.filter || {};
    const { type: userTypeFilter, ...userFilterWithoutType } = userFilter;

    const buildConversionTypeFilter = (): Record<string, unknown> => {
        const tf = userTypeFilter as Record<string, unknown> | undefined;
        if (!tf) return { not_in: NON_CONVERSION_TYPES };
        if (tf.not_in !== undefined) {
            const userNotIn = Array.isArray(tf.not_in) ? (tf.not_in as string[]) : [String(tf.not_in)];
            return { ...tf, not_in: [...new Set([...NON_CONVERSION_TYPES, ...userNotIn])] };
        }
        return { not_in: NON_CONVERSION_TYPES, ...tf };
    };

    const typeFilter = isConversionView ? { type: buildConversionTypeFilter() } : type ? { type } : {};

    const request = {
        filter: {
            filter: {
                ...typeFilter,
                ...(isConversionView ? userFilterWithoutType : userFilter),
                ...(userFilter.created_at ? {} : { created_at: { btw: createdAtStr } }),
                ...(isConversionView ? { exclude_failed_send: 1 } : {}),
            },
            include,
            sort: tableState?.filter?.sort ?? "-created_at",
        },
        page: {
            number: tableState.page?.number ?? DEFAULT_PAGE_NUMBER,
            size: tableState.page?.size ?? DEFAULT_PAGE_SIZE,
        },
    };

    const { getRowHandlers, getRowClassName } = useSmartRowHandlers<ConversionStateData>({
        entityName: "conversionState",
        detailPermission: viewDetailPermission,
        detailDrawerId:
            type === ConversionStateType.Click
                ? DrawerId.LogClickDetail
                : type === ConversionStateType.Lead
                  ? DrawerId.LogLeadDetail
                  : DrawerId.LogConversionDetail,
        detailDrawerParam:
            type === ConversionStateType.Click
                ? DrawerId.LogClickDetail
                : type === ConversionStateType.Lead
                  ? DrawerId.LogLeadDetail
                  : DrawerId.LogConversionDetail,
    });

    const activeQuery = useGetConversionStatesQuery(request, { enabled: false });
    const activeExportMutation = useGetConversionStatesExportMutation();

    const tableFetchDataUtils = useTableDataApiOperations<
        GetConversionStatesResponse,
        void,
        Record<string, never>,
        BaseExportRequest
    >(tableId, activeQuery, undefined, undefined, activeExportMutation);

    const renderTransactionId = (_: string, data: ConversionStateData) => {
        const firstPayout = data.payouts?.[0];
        const extraCount = Math.max((data.payouts?.length ?? 0) - 1, 0);

        if (!firstPayout) {
            return "-";
        }

        return (
            <div className="flex items-center gap-2">
                <ShortenedIdWithCopy id={firstPayout.id} />
                {extraCount > 0 && <Tag className="m-0">+{extraCount}</Tag>}
            </div>
        );
    };

    const renderCommissionId = (_: string, data: ConversionStateData) => {
        const firstPayout = data.payouts?.[0];
        const extraCount = Math.max((data.payouts?.length ?? 0) - 1, 0);

        if (!firstPayout?.commission_id) {
            return "-";
        }

        return (
            <div className="flex items-center gap-2">
                <ShortenedIdWithCopy id={firstPayout.commission_id} />
                {extraCount > 0 && <Tag className="m-0">+{extraCount}</Tag>}
            </div>
        );
    };

    const columnConfig: TableColumnType<ConversionStateData>[] = [
        {
            title: <ColumnHeaderTitle columnTitle={tG("date")} />,
            dataIndex: "created_at",
            key: "created_at",
            inputType: "text",
            render: (v) => <DateTimeCell value={v} />,
            sorter: true,
            defaultSorter: true,
            defaultSortOrder: "descend",
            width: 150,
            ellipsis: true,
        },
        {
            title: <ColumnHeaderTitle columnTitle={isConversionView ? tG("conversion_id") : tG("origin_id")} />,
            dataIndex: isConversionView ? "eid" : "origin_eid",
            key: isConversionView ? "eid" : "origin_eid",
            inputType: "text",
            render: (_, data) => (
                <ShortenedIdWithCopy id={(isConversionView ? data.eid : data.origin_eid) ?? undefined} />
            ),
            filterable: true,
            filterType: FILTER_TYPES.string,
            filterName: isConversionView ? "eid" : "origin_eid",
            sorter: true,
            width: 150,
            ellipsis: true,
        },
        ...(isConversionView
            ? [
                  {
                      title: <ColumnHeaderTitle columnTitle={tG("origin_id")} />,
                      dataIndex: "origin_eid",
                      key: "origin_eid",
                      inputType: "text",
                      render: (_: string, data: ConversionStateData) => (
                          <ShortenedIdWithCopy id={data.origin_eid ?? undefined} />
                      ),
                      filterable: true,
                      filterType: FILTER_TYPES.string,
                      filterName: "origin_eid",
                      sorter: true,
                      width: 150,
                      ellipsis: true,
                  },
              ]
            : []),
        ...(isConversionView
            ? [
                  {
                      title: <ColumnHeaderTitle columnTitle={t("columns.send_id")} />,
                      dataIndex: "send_eid",
                      key: "send_eid",
                      inputType: "text",
                      render: (_: string, data: ConversionStateData) => (
                          <ShortenedIdWithCopy id={data.send_eid ?? undefined} />
                      ),
                      filterable: true,
                      filterType: FILTER_TYPES.string,
                      filterName: "send_eid",
                      sorter: true,
                      width: 150,
                      ellipsis: true,
                  },
                  {
                      title: <ColumnHeaderTitle columnTitle={t("columns.transaction_id")} />,
                      dataIndex: "payouts",
                      key: "payouts",
                      inputType: "text",
                      render: renderTransactionId,
                      filterable: true,
                      filterType: FILTER_TYPES.string,
                      filterName: "payouts.id",
                      sorter: true,
                      width: 180,
                      ellipsis: true,
                  },
                  {
                      title: <ColumnHeaderTitle columnTitle={tG("commission_id")} />,
                      dataIndex: "commissions",
                      key: "commissions",
                      inputType: "text",
                      render: renderCommissionId,
                      filterable: true,
                      filterType: FILTER_TYPES.string,
                      filterName: "payouts.commission_id",
                      sorter: true,
                      width: 180,
                      ellipsis: true,
                  },
              ]
            : []),
        {
            title: <ColumnHeaderTitle columnTitle={t("columns.affiliate_cid")} />,
            dataIndex: "owner_custom_id",
            key: "owner_custom_id",
            inputType: "text",
            render: (_, data) => (data.owner_custom_id ? <ShortenedIdWithCopy id={data.owner_custom_id} /> : "-"),
            filterable: true,
            filterType: FILTER_TYPES.string,
            filterName: "owner_custom_id",
            sorter: true,
            width: 150,
            ellipsis: true,
        },
        ...(type === ConversionStateType.Lead || isConversionView
            ? [
                  {
                      title: <ColumnHeaderTitle columnTitle={tG("external_id")} />,
                      dataIndex: "advertiser_custom_id",
                      key: "advertiser_custom_id",
                      inputType: "text",
                      render: (_: string, data: ConversionStateData) =>
                          data.advertiser_custom_id ? <ShortenedIdWithCopy id={data.advertiser_custom_id} /> : "-",
                      filterable: true,
                      filterType: FILTER_TYPES.string,
                      filterName: "advertiser_custom_id",
                      sorter: true,
                      width: 150,
                      ellipsis: true,
                  },
              ]
            : []),
        {
            title: <ColumnHeaderTitle columnTitle={tG("country")} />,
            dataIndex: "country.iso3",
            key: "country.iso3",
            inputType: "text",
            editable: true,
            render: (_, data) => (data.country ? <CountryCell country={data.country} /> : "-"),
            filterable: true,
            filterType: FILTER_TYPES.countryMultiSelect,
            filterName: "country_id",
            sortName: "country.name",
            sorter: true,
            width: 105,
            ellipsis: true,
        },
        ...(isConversionView
            ? [
                  {
                      title: <ColumnHeaderTitle columnTitle={tG("source")} />,
                      dataIndex: "source",
                      key: "source",
                      inputType: "text",
                      render: (source: string) => (source ? source : "-"),
                      filterable: true,
                      filterType: FILTER_TYPES.string,
                      filterName: "source",
                      sorter: true,
                      width: 150,
                      ellipsis: true,
                  },
              ]
            : []),
        /*{
            // TODO BE missing fraud feature + is_fraud boolean needed
            title: <ColumnHeaderTitle columnTitle={t("columns.fraud")} />,
            dataIndex: "fraud_id",
            key: "fraud_id",
            inputType: "text",
            render: (v: string) => (
                <Tag
                    icon={!v ? <CheckCircleOutlined /> : <ExclamationCircleOutlined />}
                    color={!v ? "green" : "red"}
                >
                    {!v ? tGL("no") : tGL("yes")}
                </Tag>
            ),
            filterable: true,
            filterType: FILTER_TYPES.boolean,
            filterName: "fraud_id",
            sorter: true,
            width: 100,
            ellipsis: true
        },*/
        TableColumn.Entity.Owner<ConversionStateData>({
            title: <ColumnHeaderTitle columnTitle={tG("affiliate")} />,
            width: 150,
            ellipsis: true,
            getEntity: (data) => data.owner,
        }),
        TableColumn.Entity.Advertiser<ConversionStateData>({
            title: <ColumnHeaderTitle columnTitle={tG("advertiser")} />,
            filterType: FILTER_TYPES.advertiserSelect,
            width: 150,
            ellipsis: true,
            getEntity: (data) => data.advertiser,
        }),
        TableColumn.Entity.Offer<ConversionStateData>({
            title: <ColumnHeaderTitle columnTitle={tG("offer")} />,
            width: 150,
            getEntity: (data) => data.offer,
        }),
        ...(isConversionView
            ? [
                  {
                      title: <ColumnHeaderTitle columnTitle={tG("channel")} />,
                      dataIndex: "cascade.title",
                      key: "cascade.title",
                      inputType: "text",
                      render: (_: string, data: ConversionStateData) =>
                          data.cascade ? <EntityLinkCell.Cascade entity={data.cascade} /> : "-",
                      filterable: true,
                      filterType: FILTER_TYPES.cascadeSelect,
                      filterName: "cascade_id",
                      sortName: "cascade.title",
                      sorter: true,
                      width: 160,
                      ellipsis: true,
                  },
                  {
                      title: <ColumnHeaderTitle columnTitle={t("columns.method")} />,
                      dataIndex: "origin",
                      key: "origin",
                      inputType: "text",
                      render: (origin: string | null) => (origin ? origin : "-"),
                      filterable: true,
                      filterType: FILTER_TYPES.string,
                      filterName: "origin",
                      sorter: true,
                      width: 140,
                      ellipsis: true,
                  },
              ]
            : []),
        ...(type === ConversionStateType.Lead &&
        permissionsUtils.hasPermission(PermissionEnum.ConversionLogFormValuesView)
            ? [
                  {
                      title: <ColumnHeaderTitle columnTitle={t("columns.data")} />,
                      dataIndex: "formSubmissionValuesStructured",
                      key: "formSubmissionValuesStructured",
                      inputType: "text",
                      className: "overflow-hidden",
                      render: (_: string, row: ConversionStateData) => {
                          if (!row.formSubmissionValuesStructured) {
                              return <>-</>;
                          }
                          const id = `formValues-${row.id}`;
                          const parsedJson = JSON.stringify(row.formSubmissionValuesStructured);
                          return <CodeEditor id={id}>{parsedJson}</CodeEditor>;
                      },
                      // filterable: true,
                      // filterType: FILTER_TYPES.string,
                      // filterName: "formSubmissionValuesStructured",
                      // visible: false", TODO - make hidden as default - need table columns settings working
                      sorter: false,
                      width: 350,
                      ellipsis: true,
                  },
              ]
            : []),
        ...(type === ConversionStateType.Lead
            ? [
                  {
                      title: <ColumnHeaderTitle columnTitle={t("columns.process_url")} />,
                      dataIndex: "cascadeProcess",
                      key: "cascadeProcess",
                      inputType: "text",
                      render: (_: unknown, data: ConversionStateData) => {
                          const tenantSlug = userUtils.getActiveTenant()?.slug_name;
                          const processId = data.cascadeProcess?.id;
                          if (!tenantSlug || !processId) return "-";
                          return (
                              <UrlCell
                                  url={`${import.meta.env.VITE_APP_URL}/${tenantSlug}/processes/${processId}`}
                                  label={formatLongId(processId)}
                                  copyValue={processId}
                              />
                          );
                      },
                      width: 150,
                      ellipsis: true,
                  },
              ]
            : []),
        ...(type === ConversionStateType.Click
            ? [
                  {
                      title: <ColumnHeaderTitle columnTitle={t("columns.gross")} />,
                      dataIndex: "gross",
                      key: "gross",
                      inputType: "text",
                      render: (v: number) => v,
                      filterable: true,
                      filterType: FILTER_TYPES.number,
                      filterName: "gross",
                      sorter: true,
                      width: 100,
                      ellipsis: true,
                  },
              ]
            : []),
        ...(!isConversionView
            ? [
                  {
                      title: <ColumnHeaderTitle columnTitle={t("columns.ip_address")} />,
                      dataIndex: "ip",
                      key: "ip",
                      inputType: "text",
                      filterable: true,
                      filterType: FILTER_TYPES.string,
                      filterName: "ip",
                      sorter: true,
                      width: 150,
                      ellipsis: true,
                  },
              ]
            : []),
        ...(!isConversionView
            ? [
                  {
                      title: <ColumnHeaderTitle columnTitle={tG("state")} />,
                      dataIndex: "state",
                      key: "state",
                      inputType: "boolean",
                      render: (v: boolean, data: ConversionStateData) => (
                          <LeadStateCell state={v} status={data.status} />
                      ),
                      filterable: true,
                      filterType: { ...FILTER_TYPES.boolean, booleanLabels: { true: tGL("ok"), false: tGL("fail") } },
                      filterName: "state",
                      sorter: true,
                      width: 100,
                      ellipsis: true,
                  },
                  {
                      title: <ColumnHeaderTitle columnTitle={tG("status")} />,
                      dataIndex: "status",
                      key: "status",
                      render: (_: string, data: ConversionStateData) => (
                          <ConversionStatusCell status={data.status} message={data.message} />
                      ),
                      filterable: true,
                      filterType: FILTER_TYPES.conversionStateStatusMultiSelect,
                      filterName: "status",
                      filterOptions: (type === ConversionStateType.Lead
                          ? LEAD_STATUS_VALUES
                          : type === ConversionStateType.Click
                            ? CLICK_STATUS_VALUES
                            : CONVERSION_STATUS_VALUES
                      ).map((value) => ({
                          label: tGeneral(`conversion_state_status.${value}`),
                          value,
                      })),
                      sorter: true,
                      width: 180,
                      ellipsis: true,
                  },
                  {
                      title: <ColumnHeaderTitle columnTitle={t("columns.detail")} />,
                      dataIndex: "message",
                      key: "message",
                      inputType: "text",
                      render: (_: string, data: ConversionStateData) => data.message || "-",
                      filterable: true,
                      filterType: FILTER_TYPES.string,
                      filterName: "message",
                      visible: false,
                      sorter: true,
                      width: 200,
                      ellipsis: true,
                  },
                  {
                      title: <ColumnHeaderTitle columnTitle={tG("source")} />,
                      dataIndex: "source",
                      key: "source",
                      inputType: "text",
                      render: (source: string) => (source ? source : "-"),
                      filterable: true,
                      filterType: FILTER_TYPES.string, //todo make as select?
                      filterName: "source",
                      visible: false,
                      sorter: true,
                      width: 150,
                      ellipsis: true,
                  },
              ]
            : []),
        ...(isConversionView
            ? [
                  {
                      title: <ColumnHeaderTitle columnTitle={t("columns.conversion_type")} />,
                      dataIndex: "type",
                      key: "type",
                      inputType: "text",
                      render: (type: string) => (type ? type : "-"),
                      filterable: true,
                      filterType: FILTER_TYPES.conversionStateTypeMultiSelect,
                      filterOptions: conversionOnlyTypeOptions,
                      filterName: "type",
                      sorter: true,
                      width: 150,
                      ellipsis: true,
                  },
                  {
                      title: <ColumnHeaderTitle columnTitle={t("columns.transaction_type")} />,
                      dataIndex: "payouts.0.type",
                      key: "payouts.0.type",
                      inputType: "text",
                      render: (_: string, data: ConversionStateData) => data.payouts?.[0]?.type || "-",
                      filterable: true,
                      filterType: FILTER_TYPES.string,
                      filterName: "payouts.type",
                      sorter: true,
                      width: 150,
                      ellipsis: true,
                  },
                  {
                      title: <ColumnHeaderTitle columnTitle={tG("state")} />,
                      dataIndex: "state",
                      key: "state",
                      inputType: "boolean",
                      render: (v: boolean) => (
                          <Tag
                              icon={v === false ? <CloseCircleOutlined /> : <CheckCircleOutlined />}
                              color={v === false ? "red" : "green"}
                          >
                              {v === false ? tGL("fail") : tGL("ok")}
                          </Tag>
                      ),
                      filterable: true,
                      filterType: { ...FILTER_TYPES.boolean, booleanLabels: { true: tGL("ok"), false: tGL("fail") } },
                      filterName: "state",
                      sorter: true,
                      width: 100,
                      ellipsis: true,
                  },
                  {
                      title: <ColumnHeaderTitle columnTitle={t("columns.output")} />,
                      dataIndex: "eid",
                      key: "output",
                      inputType: "text",
                      render: (_: string, data: ConversionStateData) => (
                          <ShortenedIdWithCopy id={data.eid ?? undefined} />
                      ),
                      visible: false,
                      width: 150,
                      ellipsis: true,
                  },
              ]
            : []),
        ...Array.from({ length: 10 }, (_, i) => i + 1).map((num) => ({
            title: <ColumnHeaderTitle columnTitle={tG(`aff_subid${num}`)} />,
            dataIndex: `owner_data.affs${num}`,
            key: `owner_data.affs${num}`,
            inputType: "text",
            render: (_: string, record: ConversionStateData) => record.owner_data?.[`affs${num}`] || "-",
            filterable: true,
            filterType: FILTER_TYPES.string,
            filterName: `owner_data.affs${num}`,
            visible: false,
            sorter: true,
            width: 150,
            ellipsis: true,
        })),
        ...Array.from({ length: 10 }, (_, i) => i + 1).map((num) => ({
            title: <ColumnHeaderTitle columnTitle={tG(`adv_subid${num}`)} />,
            dataIndex: `advertiser_data.advs${num}`,
            key: `advertiser_data.advs${num}`,
            inputType: "text",
            render: (_: string, record: ConversionStateData) => record.advertiser_data?.[`advs${num}`] || "-",
            filterable: true,
            filterType: FILTER_TYPES.string,
            filterName: `advertiser_data.advs${num}`,
            visible: false,
            sorter: true,
            width: 150,
            ellipsis: true,
        })),
        {
            title: <ColumnHeaderTitle columnTitle={t("columns.custom_parameters")} />,
            dataIndex: "custom_data",
            key: "custom_data",
            inputType: "text",
            className: "overflow-hidden",
            render: (_: unknown, row: ConversionStateData) => {
                if (!row.custom_data || Object.keys(row.custom_data).length === 0) {
                    return <>-</>;
                }
                const id = `customData-${row.id}`;
                return <CodeEditor id={id}>{JSON.stringify(row.custom_data)}</CodeEditor>;
            },
            visible: false,
            sorter: false,
            width: 350,
            ellipsis: true,
        },
    ];

    return (
        <>
            <Table<GetConversionStatesResponse, ConversionStateData>
                tableId={tableId}
                data={tableFetchDataUtils.tableData as GetConversionStatesResponse}
                dataSource={tableFetchDataUtils.tableData?.conversions || []}
                columnConfig={columnConfig}
                loading={activeQuery.isLoading || tableFetchDataUtils.fetchQuery.isFetching}
                onRefresh={tableFetchDataUtils.refetchData}
                showRefetchButton={true}
                rowKey={(record) => record.id as Key}
                enableFilters={permissionsUtils.hasPermission(PermissionEnum.LogSelectFilter)}
                enableColumnsVisibility={permissionsUtils.hasPermission(PermissionEnum.LogColumnSetting)}
                onRow={(record, rowIndex) => getRowHandlers(record, rowIndex)}
                rowClassName={(_, rowIndex) => getRowClassName(rowIndex)}
                enableTimeFilter={permissionsUtils.hasPermission(PermissionEnum.LogSelectFilterTime)}
                dateFilterDataIndex={"created_at"}
                dateFilterMode={"minute"}
                onExport={() =>
                    tableFetchDataUtils.exportData({
                        extension: exportState.extension,
                        headers: exportState.headers,
                        export_all: exportState.range === "all",
                        page: request.page,
                        filter: request.filter,
                        delimiter: exportState.delimiter,
                    })
                }
                hideActionsColumn={true}
                autoScrollWidth={true}
            />
            {type === ConversionStateType.Click && searchParams.logClickDetail && <LogClickDrawerDetail />}
            {type === ConversionStateType.Lead && searchParams.logLeadDetail && <LogLeadDrawerDetail />}
            {type === "conversion" && searchParams.logConversionDetail && <LogConversionDrawerDetail />}
        </>
    );
};
